import { useEffect, useState } from 'react';
import { C, DEGRADE_ACTION } from '../../theme';
import { Pastille } from './base';
import { BoutonDeuxGestes } from './BoutonDeuxGestes';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';

/**
 * Les notifications de CET appareil : rappels a l'heure voulue et revue du matin.
 *
 * L'abonnement push appartient au navigateur, pas au compte : chaque appareil s'active
 * un par un. Quand le navigateur ne sait pas faire (Safari iPhone hors ecran d'accueil,
 * vieux navigateurs), on le DIT au lieu d'afficher un bouton qui ne commanderait rien.
 */

/** La cle publique VAPID (base64url) vers le format que demande pushManager.subscribe. */
function decoderClePublique(cle: string): Uint8Array<ArrayBuffer> {
  const base64 = cle.replace(/-/g, '+').replace(/_/g, '/');
  const complete = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const octets = atob(complete);
  const tampon = new Uint8Array(new ArrayBuffer(octets.length));
  for (let i = 0; i < octets.length; i += 1) tampon[i] = octets.charCodeAt(i);
  return tampon;
}

type Etat = 'verification' | 'indisponible' | 'inactif' | 'actif';

export function NotificationsAppareil({ signaler }: { signaler: (message: string) => void }) {
  const supporte =
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window;

  const [etat, setEtat] = useState<Etat>(supporte ? 'verification' : 'indisponible');
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    if (!supporte) return;
    let vivant = true;
    // getRegistration, PAS `ready` : sans service worker enregistre (le serveur de
    // developpement n'en pose pas), `ready` ne se resout jamais et la carte resterait
    // muette pour toujours. Le delai couvre les navigateurs qui trainent.
    const delai = new Promise<undefined>((resoudre) => {
      setTimeout(() => resoudre(undefined), 3000);
    });
    Promise.race([navigator.serviceWorker.getRegistration(), delai])
      .then((enregistrement) => {
        if (enregistrement === undefined) {
          if (vivant) setEtat('indisponible');
          return null;
        }
        return enregistrement.pushManager.getSubscription().then((abonnement) => {
          if (vivant) setEtat(abonnement === null ? 'inactif' : 'actif');
        });
      })
      .catch(() => {
        if (vivant) setEtat('indisponible');
      });
    return () => {
      vivant = false;
    };
  }, [supporte]);

  const activer = async () => {
    if (occupe) return;
    setOccupe(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        signaler('Les notifications sont bloquées dans les réglages du navigateur');
        return;
      }
      const enregistrement = await navigator.serviceWorker.ready;
      const { cle } = await api.clePubliqueNotifications();
      const abonnement = await enregistrement.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: decoderClePublique(cle),
      });
      const details = abonnement.toJSON();
      await api.abonnerNotifications({
        endpoint: abonnement.endpoint,
        p256dh: details.keys?.p256dh ?? '',
        auth: details.keys?.auth ?? '',
      });
      setEtat('actif');
      signaler('Notifications activées sur cet appareil');
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  const desactiver = async () => {
    if (occupe) return;
    setOccupe(true);
    try {
      const enregistrement = await navigator.serviceWorker.ready;
      const abonnement = await enregistrement.pushManager.getSubscription();
      if (abonnement !== null) {
        await api.desabonnerNotifications(abonnement.endpoint);
        await abonnement.unsubscribe();
      }
      setEtat('inactif');
      signaler('Notifications désactivées sur cet appareil');
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  const essayer = async () => {
    if (occupe) return;
    setOccupe(true);
    try {
      await api.essaiNotifications();
      signaler('Essai envoyé — elle arrive dans un instant');
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 14,
        padding: '13px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>Sur cet appareil</div>
        <div style={{ fontSize: 12, color: C.secondaire, lineHeight: 1.4 }}>
          {etat === 'indisponible'
            ? "Ce navigateur ne permet pas les notifications. Sur iPhone, ajoute d'abord Colette à l'écran d'accueil."
            : etat === 'actif'
              ? 'Activées : tes rappels et ta revue du matin arriveront ici.'
              : 'Tes rappels et ta revue du matin, au bon moment.'}
        </div>
      </div>

      {etat === 'verification' ? null : etat === 'indisponible' ? (
        <Pastille fond={C.page} couleur={C.tertiaire}>
          Indisponible
        </Pastille>
      ) : etat === 'actif' ? (
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            type="button"
            disabled={occupe}
            onClick={() => void essayer()}
            style={{
              border: `1px solid ${C.violetBordure}`,
              background: C.carte,
              color: C.violetTexte,
              borderRadius: 10,
              padding: '7px 12px',
              fontSize: 13,
              fontWeight: 800,
              fontFamily: 'inherit',
              cursor: occupe ? 'default' : 'pointer',
            }}
          >
            Essai
          </button>
          <BoutonDeuxGestes
            libelle="Couper"
            libelleArme="Confirmer ?"
            aria="Couper"
            ariaArme="Confirmer : couper les notifications"
            onConfirmer={() => void desactiver()}
            occupe={occupe}
            style={{
              border: `1px solid ${C.bordure}`,
              background: C.carte,
              color: C.secondaire,
              borderRadius: 10,
              padding: '7px 12px',
              fontSize: 13,
              fontWeight: 800,
              fontFamily: 'inherit',
              cursor: occupe ? 'default' : 'pointer',
            }}
            styleArme={{ color: C.corailTexte, borderColor: C.corailTexte }}
          />
        </div>
      ) : (
        <button
          type="button"
          disabled={occupe}
          onClick={() => void activer()}
          style={{
            border: 'none',
            background: occupe ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            borderRadius: 10,
            padding: '8px 14px',
            fontSize: 13,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: occupe ? 'default' : 'pointer',
            flexShrink: 0,
          }}
        >
          Activer
        </button>
      )}
    </div>
  );
}
